import { useTranslation } from 'react-i18next';
import type { StaffResponse } from '../../api/generated';
import type { RoomProperty } from '../../store/roomProperties/roomPropertiesSlice';
import type { FormState } from './types/patientDetailsCard.types';
import type { CompetenceNameById } from './preferredStaffHelpers';
import { RoomNeedPicker } from './RoomNeedPicker';
import { PreferredStaffPicker } from './PreferredStaffPicker';
import { Button } from '../UI/base/Button';
import styles from './PatientDetailsCard.module.css';

interface PatientDetailsFormProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  fieldErrors: Record<string, string>;
  mode: 'edit' | 'create';
  allRoomProperties: RoomProperty[];
  allStaff: StaffResponse[];
  competenceNameById: CompetenceNameById;
  isStaffLoaded: boolean;
  hasStaffError: boolean;
  onRetryStaff: () => void;
  onSave: () => Promise<void>;
}

export const PatientDetailsForm = ({
  form,
  setForm,
  fieldErrors,
  mode,
  allRoomProperties,
  allStaff,
  competenceNameById,
  isStaffLoaded,
  hasStaffError,
  onRetryStaff,
  onSave
}: PatientDetailsFormProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.cardHeader}>
        <span className={styles.cardHeaderLabel}>{t('details-card-title')}</span>
      </div>

      <div className={styles.fieldBlock}>
        <span className={styles.fieldLabelSmall}>{t('add-patient-name')}</span>
        <input
          className={`${styles.editInput} ${fieldErrors.name ? styles.inputError : ''}`}
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
        />
        {fieldErrors.name && <span className={styles.fieldError}>{fieldErrors.name}</span>}
      </div>

      <div className={styles.fieldBlock}>
        <span className={styles.fieldLabelSmall}>{t('add-patient-cpr')}</span>
        <input
          className={`${styles.editInput} ${fieldErrors.cprNumber ? styles.inputError : ''}`}
          placeholder={t('cpr-placeholder')}
          value={form.cpr}
          onChange={(e) => setForm((prev) => ({ ...prev, cpr: e.target.value }))}
        />
        {fieldErrors.cprNumber && (
          <span className={styles.fieldError}>{fieldErrors.cprNumber}</span>
        )}
      </div>

      <div className={styles.fieldBlock}>
        <span className={styles.fieldLabelSmall}>{t('add-patient-email')}</span>
        <input
          className={`${styles.editInput} ${fieldErrors.email ? styles.inputError : ''}`}
          placeholder={t('email-placeholder')}
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
        />
        {fieldErrors.email && <span className={styles.fieldError}>{fieldErrors.email}</span>}
      </div>

      <div className={styles.fieldBlock}>
        <span className={styles.fieldLabelSmall}>{t('add-patient-phone')}</span>
        <input
          className={`${styles.editInput} ${fieldErrors.phoneNumber ? styles.inputError : ''}`}
          placeholder={t('phone-placeholder')}
          value={form.phone}
          onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
        />
        {fieldErrors.phoneNumber && (
          <span className={styles.fieldError}>{fieldErrors.phoneNumber}</span>
        )}
      </div>

      <div className={styles.fieldBlock}>
        <span className={styles.fieldLabelSmall}>
          {t('add-patient-room-need')}
          <span className={styles.optionalTag}>({t('patient-detail-optional')})</span>
        </span>
        <RoomNeedPicker
          roomNeeds={form.roomNeeds}
          allRoomProperties={allRoomProperties}
          onChange={(roomNeeds) => setForm((prev) => ({ ...prev, roomNeeds }))}
        />
      </div>

      <div className={styles.fieldBlock}>
        <PreferredStaffPicker
          preferredStaffIds={form.preferredStaffIds}
          allStaff={allStaff}
          competenceNameById={competenceNameById}
          isStaffLoaded={isStaffLoaded}
          hasStaffError={hasStaffError}
          onRetryStaff={onRetryStaff}
          onChange={(preferredStaffIds) => setForm((prev) => ({ ...prev, preferredStaffIds }))}
        />
      </div>

      <div className={styles.fieldBlock}>
        <span className={styles.fieldLabelSmall}>
          {t('add-patient-special-requirements')}
        </span>
        <textarea
          className={styles.editCommentArea}
          placeholder={t('comment-placeholder')}
          value={form.comment}
          onChange={(e) => setForm((prev) => ({ ...prev, comment: e.target.value }))}
        />
      </div>

      <div className={styles.actionButtons}>
        <Button
          type="secondary"
          onClick={onSave}
          disabled={!form.name || !form.cpr}
        >
          {t(mode === 'create' ? 'create-new' : 'update-patient')}
        </Button>
      </div>
    </>
  );
};
